/**
 * @fileoverview One walkthrough film: the video, its steps as chapters, and a
 * link to share it.
 *
 * Used on the film's own page (/academy/onboarding/:slug) and on any feature page that
 * leads with a walkthrough, so the two always play and share the same way.
 * The chapters are the film's steps: pressing one jumps there, and the step
 * playing is marked as it plays, so someone who came for "Add prep notes"
 * doesn't sit through the rest. The shared link always points at the film's
 * own page, never at the page it happens to be embedded in.
 */

import { useEffect, useRef, useState } from "react";
import { Check, Link2 } from "lucide-react";

import { formatDuration, walkthroughPath } from "@/lib/walkthroughs";

/** The chapter playing at `t` seconds: the last one that has started. */
function chapterAt(chapters, t) {
  let current = 0;
  chapters.forEach((c, i) => {
    if (t >= c.at) current = i;
  });
  return current;
}

export default function WalkthroughPlayer({ walkthrough, startAt = 0 }) {
  const video = useRef(null);
  const [current, setCurrent] = useState(chapterAt(walkthrough.chapters, startAt));
  const [copied, setCopied] = useState(false);

  // A shared link that names a moment (?t=) starts there, without autoplaying.
  useEffect(() => {
    const el = video.current;
    if (!el || startAt <= 0) return undefined;
    const seek = () => {
      el.currentTime = startAt;
    };
    if (el.readyState >= 1) seek();
    else el.addEventListener("loadedmetadata", seek, { once: true });
    return () => el.removeEventListener("loadedmetadata", seek);
  }, [startAt]);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const jump = (at) => {
    const el = video.current;
    if (!el) return;
    el.currentTime = at;
    setCurrent(chapterAt(walkthrough.chapters, at));
    el.play?.()?.catch?.(() => {});
  };

  const copyLink = async () => {
    const url = `${window.location.origin}${walkthroughPath(walkthrough.slug)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard (an insecure context or a refusing browser): the address
      // is still the film's page, so send the reader there to copy it.
      window.location.assign(walkthroughPath(walkthrough.slug));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_280px]" data-testid="walkthrough-player">
      <div className="min-w-0">
        <div className="overflow-hidden rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] shadow-2xl">
          {/* Narration captions ride in the track, off until the viewer turns
              them on; the picture already carries each step's instruction. */}
          <video
            ref={video}
            className="block aspect-video w-full"
            controls
            playsInline
            preload="metadata"
            poster={walkthrough.poster}
            onTimeUpdate={(e) => setCurrent(chapterAt(walkthrough.chapters, e.currentTarget.currentTime))}
            data-testid="walkthrough-video"
          >
            <source src={walkthrough.src} type="video/mp4" />
            <track kind="captions" src={walkthrough.captions} srcLang="en" label="English" />
            Your browser can&apos;t play this video.{" "}
            <a href={walkthrough.src}>Download it</a> instead.
          </video>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[12px] uppercase tracking-widest text-[var(--text-tertiary)]">
            {formatDuration(walkthrough.seconds)} · {walkthrough.chapters.length - 1} steps · captions available
          </p>
          <button
            type="button"
            onClick={copyLink}
            data-testid="walkthrough-copy-link"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-2 font-sans text-sm text-[var(--text-primary)] transition duration-200 hover:bg-[var(--studio-overlay)] active:scale-95"
          >
            {copied ? <Check className="h-4 w-4" strokeWidth={1.5} /> : <Link2 className="h-4 w-4" strokeWidth={1.5} />}
            {copied ? "Link copied" : "Copy link"}
          </button>
        </div>
      </div>

      <nav aria-label="Steps in this walkthrough">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-[var(--text-tertiary)]">Steps</p>
        <ol className="space-y-1">
          {walkthrough.chapters.map((chapter, i) => (
            <li key={chapter.at}>
              <button
                type="button"
                onClick={() => jump(chapter.at)}
                aria-current={i === current ? "step" : undefined}
                data-testid={`walkthrough-chapter-${i}`}
                className={[
                  "flex w-full items-baseline gap-3 rounded-xl px-3 py-2 text-left transition duration-200 active:scale-95",
                  i === current
                    ? "bg-[var(--studio-raised)] text-[var(--text-primary)]"
                    : "text-[var(--text-secondary)] hover:bg-[var(--studio-raised)] hover:text-[var(--text-primary)]",
                ].join(" ")}
              >
                <span className="w-9 shrink-0 font-mono text-[12px] text-[var(--accent-yellow-text)]">{formatDuration(chapter.at)}</span>
                <span className="font-sans text-sm">{chapter.title}</span>
              </button>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
}
